/* eslint-disable max-len */
import React, {
  useEffect,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from '@thd-olt-component-react/grid';
import { Button } from '@thd-olt-component-react/button';
import { useAisleBayTextMeDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { AisleBayTextMeFooter } from './AisleBayTextMeFooter';
import {
  cleanNumber,
  formatPhoneNumber,
  isValidPhoneNumber,
  publish
} from '../helper';
import './aisle-bay.scss';

const HEADER_MOBILE_ICON = (
  <svg viewBox="0 0 25 40">
    <path
      d="M24.4527279.563498H0v40h24.4527279v-40zM9.9402471 32.327923h4.5702285v4.35069H9.9402471v-4.35069zm10.178781-14.896196v10.856679H4.3336992V4.688922h15.7853289v12 .742805z"
      fill="#fb6300"
      fillRule="evenodd"
    />
  </svg>
);
const TEMPLATES = {
  templateForInvalidPhone: (
    <span className="smartError-color">Please enter a valid 10-digit phone number.</span>
  ),
  templateForSubscribeSuccess: (
    <div>
      <div className="succeess__subscribe--icon" />
      <div className="succeess__subscribe--header">{'We\'ll text you shortly'}</div>
      <span className="succeess__subscribe--content">
        {'You\'ll receive a text with product\'s store location from 20841 and a text from 65624 to finish signing up for The Home Depot Text Program (and get your'}
        <span className="u__bold"> $5 off coupon</span>)
      </span>
    </div>
  ),
  temlateForTextSuccess: (
    <div>
      <div className="succeess__icon" />
      <div className="succeess--content">
        {'A text message with the product\'s in-store location will arrive shortly. See you soon!'}
      </div>
    </div>
  ),
  templateForTextFailure: (
    <span className="smartError-color">
      {'We\'re sorry. We\'re experiencing technical difficulties. Please try again in a few minutes.'}
    </span>
  )
};

const AisleBayTextMe = forwardRef((props, ref) => {
  const { itemId, storeId, aisleBayText } = props;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [status, setStatus] = useState('');

  const resetState = () => {
    setPhoneNumber('');
    setIsSubscribed(false);
    setStatus('');
  };

  useImperativeHandle(ref, () => ({ resetState }));

  const [makeRequest, response] = useAisleBayTextMeDeprecated({
    itemId,
    storeId,
    aisleBayText,
    phoneNumber: phoneNumber.replace(/-/g, ''),
    isSavingsSubscribed: isSubscribed
  }, 'aisleBayTextMe');
  const result = response?.data?.aisleBayTextMe?.result;

  useEffect(() => {
    if (result === 'opt_in_required' || result === 'success') {
      setStatus('SUCCESS');
      publish('success', { type: 'success' });
    }
    if (result === '' || result === 'error') {
      setStatus('ERROR');
      publish('error', {
        type: 'error',
        data: {
          code: 500,
          message: 'Internal Server Error'
        }
      });
    }
  }, [result, setStatus]);

  const handleTextme = useCallback((ev) => {
    ev.preventDefault();
    if (!isValidPhoneNumber({ phoneNumber: cleanNumber(phoneNumber) })) {
      setStatus('INVALID_PHONE_NUMBER');
    } else {
      makeRequest({
        variables: {
          itemId,
          storeId,
          aisleBayText,
          phoneNumber: cleanNumber(phoneNumber),
          isSavingsSubscribed: isSubscribed
        }
      });
    }
  }, [setStatus, phoneNumber, makeRequest, isSubscribed]);

  const handleChange = useCallback((ev) => {
    const { value } = ev.target;
    let formatted;
    if (ev.nativeEvent.inputType === 'deleteContentBackward') {
      const cleanedValue = cleanNumber(phoneNumber);
      const lastNumRemoved = cleanedValue.slice(0, cleanedValue.length - 1);
      formatted = formatPhoneNumber(lastNumRemoved);
    } else {
      const cleanedValue = cleanNumber(value);
      formatted = formatPhoneNumber(cleanedValue);
    }
    setPhoneNumber(formatted);
  }, [setPhoneNumber]);

  return (
    <Row className="aisleBay__TextMe--container">
      <Col fallback="12" className="aislebay-header">
        <div className="tooltip_header">
          <div>
            <span className="aisle-and-bay-tooltip__mobile">
              {HEADER_MOBILE_ICON}
            </span>
          </div>
          <div className="aislebay__header--title">Text Product Location to Your Phone</div>
        </div>
      </Col>
      <Col fallback="12" className="tooltip_inner">
        {['SUCCESS', 'ERROR'].indexOf(status) === -1 && (
          <form>
            <div className="aislebay--title">Mobile Phone:</div>
            <div className="grid">
              <div className="col__7-12 textme--input">
                <input
                  type="tel"
                  placeholder="___-___-____"
                  className={`${status === 'INVALID_PHONE_NUMBER' ? 'aislebay__text--Error' : ''} form-input__field`}
                  value={phoneNumber}
                  name="phoneNumber"
                  onChange={handleChange}
                  autoComplete="off"
                  maxLength="12"
                />
              </div>
              <div className="col__5-12">
                <Button onClick={handleTextme} dark outline>
                  Text Me
                </Button>
              </div>
              {status === 'INVALID_PHONE_NUMBER'
              && TEMPLATES.templateForInvalidPhone}
            </div>
            <div className="checkbox-btn aislebay__subscription--container">
              <input
                className="checkbox-btn__input"
                onChange={() => setIsSubscribed(!isSubscribed)}
                type="checkbox"
                id="subscriptionAgreement"
                name="subscriptionAgreement"
                checked={isSubscribed}
              />
              {/* eslint-disable-next-line */}
              <label
                className="checkbox-btn__label"
                htmlFor="subscriptionAgreement"
              >
                Yes, I want <span className="u__bold termsAndConditionsColorText">$5 off my next in store purchase</span>
                {' and receive $100s more in savings right to my phone by joining the Home Depot Text Program*'}
              </label>
            </div>
          </form>
        )}
        {status === 'SUCCESS' && isSubscribed
          && TEMPLATES.templateForSubscribeSuccess}
        {status === 'SUCCESS' && !isSubscribed
          && TEMPLATES.temlateForTextSuccess}
        {status === 'ERROR'
          && TEMPLATES.templateForTextFailure}
      </Col>
      <Col fallback="12" className="termsAndConditionsContainer">
        <AisleBayTextMeFooter />
      </Col>
    </Row>
  );
});

AisleBayTextMe.propTypes = {
  aisleBayText: PropTypes.string,
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string
};

AisleBayTextMe.defaultProps = {
  aisleBayText: '',
  storeId: null
};

export default AisleBayTextMe;
